import type { HistoryItem } from "./types";

export const LEGACY_LOCAL_HISTORY_KEY = "backtest.web.localHistory";
export const LOCAL_HISTORY_MIGRATION_KEY = "backtest.web.localHistory.scoped.v1.migrated";
const LOCAL_HISTORY_KEY_PREFIX = "backtest.web.localHistory.scoped.v1";

export interface LocalHistoryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function localHistoryStorageScope(accountID?: string | null): string {
  return accountID ? `account:${accountID}` : "guest";
}

export function localHistoryStorageKey(accountID?: string | null): string {
  if (!accountID) return `${LOCAL_HISTORY_KEY_PREFIX}.guest`;
  return `${LOCAL_HISTORY_KEY_PREFIX}.account.${encodeURIComponent(accountID)}`;
}

function parseHistory(raw: string | null, limit: number): HistoryItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, limit) : [];
  } catch {
    return [];
  }
}

function mergeHistoryForMigration(
  primary: HistoryItem[],
  secondary: HistoryItem[],
  limit: number,
): HistoryItem[] {
  const seen = new Set<string>();
  return [...primary, ...secondary]
    .filter((item) => {
      const key = item?.result?.run_id || item?.id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )
    .slice(0, limit);
}

function migrateLegacyHistoryToGuest(
  storage: LocalHistoryStorage,
  limit: number,
): void {
  if (storage.getItem(LOCAL_HISTORY_MIGRATION_KEY) === "true") return;

  const legacyRaw = storage.getItem(LEGACY_LOCAL_HISTORY_KEY);
  if (legacyRaw) {
    const guestKey = localHistoryStorageKey(null);
    const migrated = mergeHistoryForMigration(
      parseHistory(storage.getItem(guestKey), limit),
      parseHistory(legacyRaw, limit),
      limit,
    );
    try {
      storage.setItem(guestKey, JSON.stringify(migrated));
    } catch {
      // Keep the legacy value and retry on a later guest load. Never mark a
      // migration complete when its destination could not be persisted.
      return;
    }
  }

  try {
    storage.setItem(LOCAL_HISTORY_MIGRATION_KEY, "true");
    storage.removeItem(LEGACY_LOCAL_HISTORY_KEY);
  } catch {
    // A stale legacy copy is harmless: the marker prevents it from being
    // imported again if the marker write succeeded.
  }
}

export function loadLocalHistoryForAccount(
  storage: LocalHistoryStorage,
  accountID: string | null | undefined,
  limit: number,
): HistoryItem[] {
  if (!accountID) {
    migrateLegacyHistoryToGuest(storage, limit);
  }
  return parseHistory(storage.getItem(localHistoryStorageKey(accountID)), limit);
}

export function saveLocalHistoryForAccount(
  storage: LocalHistoryStorage,
  accountID: string | null | undefined,
  items: HistoryItem[],
  limit: number,
): boolean {
  try {
    storage.setItem(
      localHistoryStorageKey(accountID),
      JSON.stringify(items.slice(0, limit)),
    );
    return true;
  } catch {
    // Local history is a convenience cache; quota/private-mode failures should
    // never block a backtest.
    return false;
  }
}

/**
 * Copies (never moves) anonymous history into a newly-created account scope.
 * Existing-account sign-in must not call this helper.
 */
export function copyGuestHistoryToNewAccount(
  storage: LocalHistoryStorage,
  accountID: string,
  limit: number,
): HistoryItem[] {
  const guestItems = loadLocalHistoryForAccount(storage, null, limit);
  const accountItems = loadLocalHistoryForAccount(storage, accountID, limit);
  const merged = mergeHistoryForMigration(guestItems, accountItems, limit);
  saveLocalHistoryForAccount(storage, accountID, merged, limit);
  return merged;
}
